/**
 * StudioRegistration
 *
 * Contrat déclaratif générique utilisé pour enregistrer un Studio auprès
 * des consommateurs de la plateforme, notamment le framework Admin.
 *
 * Une registration décrit uniquement :
 * - l'identité du Studio ;
 * - son mode de panneau Admin ;
 * - son chargeur de composant Studio ;
 * - son composant d'intégration Admin ;
 * - ses métadonnées techniques génériques.
 *
 * Elle ne contient aucun état React, hook, runtime, adapter, persistence
 * ou comportement métier.
 */

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim() !== "";
}

function isComponentType(value) {
  return (
    typeof value === "function"
    || (
      value !== null
      && typeof value === "object"
    )
  );
}

export function createStudioRegistration({
  id,
  panelMode,
  loadStudioComponent,
  AdminPanelComponent,
  keyPrefix = id,
  metadata = {},
} = {}) {
  if (!isNonEmptyString(id)) {
    throw new TypeError(
      "StudioRegistration.id must be a non-empty string."
    );
  }

  if (!isNonEmptyString(panelMode)) {
    throw new TypeError(
      "StudioRegistration.panelMode must be a non-empty string."
    );
  }

  if (typeof loadStudioComponent !== "function") {
    throw new TypeError(
      "StudioRegistration.loadStudioComponent must be a function."
    );
  }

  if (!isComponentType(AdminPanelComponent)) {
    throw new TypeError(
      "StudioRegistration.AdminPanelComponent must be a valid component type."
    );
  }

  if (!isNonEmptyString(keyPrefix)) {
    throw new TypeError(
      "StudioRegistration.keyPrefix must be a non-empty string."
    );
  }

  if (
    metadata === null
    || typeof metadata !== "object"
    || Array.isArray(metadata)
  ) {
    throw new TypeError(
      "StudioRegistration.metadata must be an object."
    );
  }

  return Object.freeze({
    id: id.trim(),
    panelMode: panelMode.trim(),
    loadStudioComponent,
    AdminPanelComponent,
    keyPrefix: keyPrefix.trim(),
    metadata: Object.freeze({ ...metadata }),
  });
}

export function isStudioRegistration(value) {
  return Boolean(
    value
      && typeof value === "object"
      && isNonEmptyString(value.id)
      && isNonEmptyString(value.panelMode)
      && typeof value.loadStudioComponent === "function"
      && isComponentType(value.AdminPanelComponent)
      && isNonEmptyString(value.keyPrefix)
      && value.metadata !== null
      && typeof value.metadata === "object"
      && !Array.isArray(value.metadata)
  );
}
